import React from "react";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import update from "immutability-helper";
import { get, size, map, filter, max, forEach } from "lodash";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";

import { fetchPost } from "../../api/fetch";
import ScrollableTable from "../Common/Table/ScrollableTable";
import ProfileLogRow from "./ProfileLogRow";
import LogDetailsDialog from "./LogDetailsDialog";
import ProfileLogCsvExport from "./ProfileLogCsvExport";
import { eventGroupsData } from "../../constants/eventGroups";
import { fetchCreatedByUsers } from "../../utils/event";
import { getLov } from "../../utils/lov";
import { isAllowed } from "../../utils/users";
import colors from "../../assets/style/colors";
import images from "../../assets/img/images";

const styles = (theme) => ({
  card: {
    backgroundColor: colors.white,
    height: "calc(100% - 24px)",
    overflow: "auto",
    borderRadius: "2px",
    [theme.breakpoints.up("sm")]: {
      maxHeight: "calc(100vh - 200px)",
      height: "calc(100vh - 200px)",
      margin: "12px",
      boxShadow: "0px 2px 2px 0px " + colors.black23,
    },
    [theme.breakpoints.down("sm")]: {
      margin: "12px 6px",
      maxHeight: "calc(100vh - 160px)",
      height: "calc(100vh - 160px)",
    },
  },
  contentContainer: {
    padding: "12px",
  },
  tableContainer: {
    maxWidth: "1920px",
    width: "calc(100% - 24px)",
    overflow: "auto",
    margin: "12px",
    boxShadow: "0px 2px 2px 0px " + colors.black23,
    backgroundColor: colors.white,
  },
  totalItem: {
    marginRight: "40px",
    "& span": {
      padding: "0px 5px",
    },
  },
});

const ProfileLog = (props) => {
  const { classes, profile, language, isMe, permissions } = props;
  const [logs, setLogs] = React.useState([]);
  const [isFetching, setIsFetching] = React.useState(false);
  const [total, setTotal] = React.useState();
  const [lov, setLov] = React.useState();
  const [counts, setCounts] = React.useState({});
  const [openedLog, setOpenedLog] = React.useState();

  React.useEffect(() => {
    const fetchLov = async () => {
      setLov({
        activityTypes: await getLov("eventGroupTypes"),
        105: await getLov(105),
      });
    };
    fetchLov();
    fetchLogs();
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLogs = async (refresh = false) => {
    if (!isFetching) {
      try {
        setIsFetching(true);
        let body = {
          filter: {
            profile_id: get(profile, ["id"]),
            types: [2, 3, 4, 5],
          },
          pagination: {
            offset: refresh ? 0 : size(logs),
            limit: 20,
          },
          data: eventGroupsData,
        };
        let res = await fetchPost("new/event_groups", body);
        res.items = await fetchCreatedByUsers(get(res, ["items"]));
        if (refresh) {
          setLogs(res.items);
        } else {
          setLogs(
            update(logs, {
              $push: res.items,
            })
          );
        }
        setTotal(get(res, ["total"]));
      } catch (e) {
        console.log(e);
      }
      setIsFetching(false);
    }
  };

  const fetchCounts = async () => {
    try {
      let res = {};
      let body = {
        filter: {
          profile_id: get(profile, ["id"]),
        },
        pagination: {
          offset: 0,
          limit: 1,
        },
        data: ["id"],
      };
      for (let i = 2; i < 6; i++) {
        body.filter.types = [i];
        res[i] = get(await fetchPost("new/event_groups", body), ["total"]);
      }
      setCounts(res);
    } catch (e) {
      console.log(e);
    }
  };

  let columns = [
    {
      name: "site_ref",
      minWidth: 100,
    },
    {
      name: "profiles_dateTime",
      minWidth: 150,
    },
    {
      name: "common_site",
      minWidth: 150,
    },
    {
      name: "common_zone",
      minWidth: 150,
    },
    {
      name: "profiles_logType",
      minWidth: 150,
    },
    {
      name: "entryStatus",
      id: 105,
      minWidth: 150,
    },
    {
      name: "profiles_humanProfile",
      icon: images.humanIconBlack,
      minWidth: 180,
    },
    {
      name: "profiles_vehicleProfile",
      icon: images.truckIconBlack,
      minWidth: 180,
    },
  ];

  let profileTypesCounts = {};
  forEach([3, 4], (type) => {
    profileTypesCounts[type] = max(
      map(logs, (log) =>
        size(
          filter(
            get(log, ["events"]),
            (e) => e.active && get(e, ["profile", "type", "id"]) === type
          )
        )
      )
    );
    for (let i = 0; i < profileTypesCounts[type]; i++) {
      if (type === 3) {
        columns.push({
          name2: `${I18n.get("profiles_trailerProfile")} ${i + 1}`,
          name: "trailer",
          icon: images.trailerIconBlack,
          minWidth: 180,
        });
      } else if (type === 4) {
        columns.push({
          name2: `${I18n.get("profiles_containerProfile")} ${i + 1}`,
          name: "container",
          icon: images.containerIconBlack,
          minWidth: 200,
        });
      }
    }
  });

  columns.push({
    name: "site_modifiedOn",
    minWidth: 150,
  });

  columns.push({
    name: "profiles_processedBy",
    minWidth: 150,
  });

  columns.push({
    name: "profiles_comments",
    minWidth: 250,
  });

  const renderTotalItem = (id) => {
    return (
      <Grid item className={classes.totalItem}>
        <span className="text-87 bold-500">
          {id
            ? get(
              filter(
                get(lov, ["activityTypes"]),
                (type) => get(type, ["id"]) === id
              ),
              [0, `name_${language}`]
            )
            : I18n.get("profiles_logEntries")}
          :
        </span>
        <span className="text-54">{id ? get(counts, [id]) : total}</span>
      </Grid>
    );
  };

  return (
    <Box className={classes.card}>
      <Grid container direction="column" className={classes.contentContainer}>
        <Grid
          item
          style={{ paddingLeft: "7px", maxWidth: "1940px", margin: "12px 0px" }}
        >
          {size(get(lov, ["activityTypes"])) && (
            <Grid container>
              {renderTotalItem()}
              {renderTotalItem(2)}
              {renderTotalItem(3)}
              {renderTotalItem(4)}
              {renderTotalItem(5)}
              <Grid
                item
                xs
                style={{
                  textAlign: "right",
                  paddingRight: "12px",
                  maxHeight: "20px",
                }}
              >
                <ProfileLogCsvExport
                  columns={columns}
                  language={language}
                  lov={lov}
                  profile={profile}
                  profileTypesCounts={profileTypesCounts}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item className={classes.tableContainer}>
          <ScrollableTable
            columns={columns}
            lazyLoading
            fetchData={fetchLogs}
            hasMore={total > size(logs)}
            isFetching={isFetching}
            containerHeight={"calc(100vh - 292px)"}
            stickyHeader
          >
            {map(logs, (item, index) => (
              <ProfileLogRow
                key={index}
                index={index}
                item={item}
                language={language}
                lov={lov}
                openDetails={() => setOpenedLog(get(item, ["id"]))}
                profileTypesCounts={profileTypesCounts}
              />
            ))}
          </ScrollableTable>
        </Grid>
        <LogDetailsDialog
          open={Boolean(openedLog)}
          onClose={() => {
            fetchLogs(true);
            setOpenedLog();
          }}
          logId={openedLog}
          blockLevel2
          noLink
          permissions={{
            read:
              !isMe || isAllowed(permissions, "me.historylog.eventgroup.read"),
            update:
              !isMe ||
              isAllowed(permissions, "me.historylog.eventgroup.update"),
            override:
              !isMe ||
              isAllowed(permissions, "me.historylog.eventgroup.override"),
            notes: {
              read:
                !isMe ||
                isAllowed(permissions, "me.historylog.eventgroup.notes.read"),
              create:
                !isMe ||
                isAllowed(permissions, "me.historylog.eventgroup.notes.create"),
            },
          }}
        />
      </Grid>
    </Box>
  );
};

export default withStyles(styles)(ProfileLog);
